// CustomExportButton.tsx
import { useContext, useMemo, useState } from "react";

import { CsvExportMenuItemWithIcon } from "./CsvExportMenuItemWithIcon";
import { GridExcelExportMenuItemCompat } from "./GridExcelExportMenuItemCompat";
import {
	GetLanguageValueAsString,
	Translate,
} from "@/app/application/Translate";
import { TranslationContext } from "@/app/providers";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import {
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Select,
	Switch,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import jsPDF from "jspdf";
import autoTable, {
	CellHookData,
	RowInput,
	UserOptions,
} from "jspdf-autotable";

dayjs.extend(utc);

/* ---------------- Types ---------------- */
type AnyRow = Record<string, unknown>;
export type AnyCol = {
	field: string;
	headerName?: string;
	header?: unknown;
	hide?: boolean;
	minWidth?: number;
};

export interface PdfMeta {
	title?: string;
	subtitle?: string;
	url?: string;
	logoDataUrl?: string;
	logoUrl?: string;
	dateFormat?: string;
	footerText?: string;
	truncateAt?: number;
	pageSize?: "a4" | "a3";
}

export interface PdfExportOptions {
	selectedColumns?: AnyCol[];
	dateFormat?: string;
	footerText?: string;
	truncateAt?: number;
	pageSize?: "a4" | "a3";
	groupBy?: string;
}

export interface PdfExportDialogState {
	dateFormat: string;
	footerText: string;
	truncateAt: number;
	truncateEnabled: boolean;
	pageSize: "a4" | "a3";
	groupBy: string;
}

interface Props {
	rows: AnyRow[];
	columns: AnyCol[];
	fileName?: string;
	pdfMeta?: PdfMeta;
	showPdfOptions?: boolean;
}

/* ---------------- Palette (matches archive/shift table) ---------------- */
const COLORS = {
	ink: [15, 23, 42] as [number, number, number],
	softInk: [71, 85, 105] as [number, number, number],
	band: [248, 250, 252] as [number, number, number],
	head: [236, 246, 255] as [number, number, number],
	headText: [15, 23, 42] as [number, number, number],
	headBorder: [203, 213, 225] as [number, number, number],
	rowAlt: [248, 250, 253] as [number, number, number],
	rule: [226, 232, 240] as [number, number, number],
	linkBlue: [31, 128, 255] as [number, number, number],
	metaKey: [100, 116, 139] as [number, number, number],
	primary: [31, 128, 255] as [number, number, number],
};

/* ---------------- Column helpers ---------------- */
export const headerOf = (c: AnyCol): string =>
	typeof c.headerName === "string"
		? c.headerName
		: typeof c.header === "string"
			? c.header
			: c.field;

const asText = (v: unknown): string => {
	if (v == null) return "";
	if (typeof v === "string") return v;
	if (typeof v === "number") return String(v);
	if (typeof v === "boolean") return v ? "Yes" : "No";
	if (v instanceof Date) return v.toISOString();
	try {
		return JSON.stringify(v);
	} catch {
		return "";
	}
};

const isRichHtmlCol = (c: AnyCol) =>
	/description|comment/i.test(c.field) ||
	/description|comment/i.test(headerOf(c));
const isAttachmentCol = (c: AnyCol) =>
	/attachment/i.test(c.field) || /attachment/i.test(headerOf(c));
const isCenterAlignCol = (c: AnyCol) =>
	/outofservice|handover|isoutofservice|ishandover/i.test(c.field) ||
	/out of service|handover/i.test(headerOf(c));
const isDateCol = (c: AnyCol) =>
	/date|created|time/i.test(c.field) || /date|created|time/i.test(headerOf(c));
const isGroupableCol = (c: AnyCol) =>
	/GroupText|LoggedByText|ShiftCrewText|ShiftName|SystemName|TagsFlat/i.test(
		c.field,
	) || /group|author|crew|shift|system|tag/i.test(headerOf(c));

/* ---------------- URL utils ---------------- */
function safeEncodeSegment(seg: string): string {
	try {
		return encodeURIComponent(decodeURIComponent(seg));
	} catch {
		return encodeURIComponent(seg);
	}
}
function encodePath(pathname: string): string {
	const q = pathname.indexOf("?"),
		h = pathname.indexOf("#");
	const end = q === -1 ? (h === -1 ? pathname.length : h) : q;
	const base = pathname.slice(0, end),
		rest = pathname.slice(end);
	return (
		base
			.split("/")
			.map((s) => (s ? safeEncodeSegment(s) : s))
			.join("/") + rest
	);
}
function toAbsoluteUrl(urlOrPath: string): string {
	const origin = window.location.origin || "https://localhost:5001";
	try {
		const u = new URL(urlOrPath, origin);
		return `${u.protocol}//${u.host}${encodePath(u.pathname)}${u.search || ""}${u.hash || ""}`;
	} catch {
		const p = urlOrPath.startsWith("/") ? urlOrPath : `/${urlOrPath}`;
		return origin + encodePath(p);
	}
}
function resolveAttachmentHref(row: AnyRow): string | undefined {
	const raw =
		row.Filepath ||
		row.FilePath ||
		row.AttachmentUrl ||
		row.AttachmentURL ||
		row.AttachmentLink;
	return raw != null ? toAbsoluteUrl(asText(raw)) : undefined;
}
function resolveAttachmentLabel(row: AnyRow, href?: string): string {
	const label =
		row.Filename ||
		row.AttachmentText ||
		(typeof row.AttachmentName === "string" && row.AttachmentName);
	if (label) return asText(label);
	if (!href) return "Attachment";
	try {
		const u = new URL(href);
		return u.pathname.split("/").filter(Boolean).pop() || href;
	} catch {
		return href.split("/").filter(Boolean).pop() || href;
	}
}

/* ---------------- SVG/IMG → PNG (hi-res, keeps aspect) ---------------- */
async function svgOrImageToPng(
	url: string,
	rasterScale = 4, // upsample factor for sharp downscale
): Promise<{ dataUrl: string; w: number; h: number }> {
	const loadImg = (src: string) =>
		new Promise<HTMLImageElement>((resolve, reject) => {
			const el = new Image();
			el.crossOrigin = "anonymous";
			el.onload = () => resolve(el);
			el.onerror = reject;
			el.src = src;
		});

	if (!/\.svg($|\?)/i.test(url)) {
		const img = await loadImg(url);
		const canvas = document.createElement("canvas");
		const w = (img.naturalWidth || img.width || 1) * rasterScale;
		const h = (img.naturalHeight || img.height || 1) * rasterScale;
		canvas.width = Math.max(1, Math.round(w));
		canvas.height = Math.max(1, Math.round(h));
		const ctx = canvas.getContext("2d")!;
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = "high";
		ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
		return {
			dataUrl: canvas.toDataURL("image/png"),
			w: canvas.width,
			h: canvas.height,
		};
	}

	// SVG: fetch and rasterize
	const res = await fetch(url, { credentials: "include", mode: "cors" });
	const svgText = await res.text();
	const blob = new Blob([svgText], { type: "image/svg+xml" });
	const svgUrl = URL.createObjectURL(blob);
	const img = await loadImg(svgUrl);

	try {
		// get aspect from intrinsic viewBox if available
		let vw = img.naturalWidth || img.width || 320;
		let vh = img.naturalHeight || img.height || 120;
		if (svgText.includes("viewBox")) {
			const m = svgText.match(
				/viewBox\s*=\s*["']\s*([\d.+-]+)\s+([\d.+-]+)\s+([\d.+-]+)\s+([\d.+-]+)\s*["']/i,
			);
			if (m) {
				vw = parseFloat(m[3]);
				vh = parseFloat(m[4]);
			}
		}
		const canvas = document.createElement("canvas");
		canvas.width = Math.max(1, Math.round(vw * rasterScale));
		canvas.height = Math.max(1, Math.round(vh * rasterScale));
		const ctx = canvas.getContext("2d")!;
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = "high";
		ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
		return {
			dataUrl: canvas.toDataURL("image/png"),
			w: canvas.width,
			h: canvas.height,
		};
	} finally {
		URL.revokeObjectURL(svgUrl);
	}
}

/* ---------------- Header (compact, modern) ---------------- */
async function drawHeader(
	doc: jsPDF,
	pageWidth: number,
	meta: PdfMeta | undefined,
	fileName: string,
	stats: { count: number },
) {
	type PdfDocWithExtras = jsPDF & {
		roundedRect: (
			x: number,
			y: number,
			w: number,
			h: number,
			rx: number,
			ry: number,
			style: "S" | "F" | "FD" | "DF",
		) => void;
		addImage: (
			imageData: string,
			format: string,
			x: number,
			y: number,
			width: number,
			height: number,
			alias?: string,
			compression?: string,
		) => void;
	};
	const pdf = doc as PdfDocWithExtras;
	const margin = 28;
	const bandH = 32;
	const bandY = margin - 2;
	const bandX = margin - 2;
	const bandW = pageWidth - 2 * bandX;

	// Compact header band
	doc.setFillColor(...COLORS.band);
	pdf.roundedRect(bandX, bandY, bandW, bandH, 6, 6, "F");

	const leftX = margin;
	const baseY = margin + 8;
	const rightPad = margin;
	const rightBoxW = 140;
	const rightX = pageWidth - rightPad - rightBoxW;

	// Title · Subtitle (single line, compact)
	const title = meta?.title || fileName || "Export";
	const subtitle = meta?.subtitle || "";
	const titleLine = subtitle ? `${title} · ${subtitle}` : title;
	doc.setTextColor(...COLORS.ink);
	doc.setFont("helvetica", "bold");
	doc.setFontSize(12);
	doc.text(titleLine, leftX, baseY);

	// Meta line: origin | generated | records (compact, single row)
	const host = meta?.url
		? (() => {
				try {
					return new URL(meta.url).host;
				} catch {
					return meta.url;
				}
			})()
		: "";
	const ts = dayjs().format("DD/MM/YY HH:mm:ss");
	const metaLine = [host, ts, `${stats.count} records`]
		.filter(Boolean)
		.join(" · ");
	doc.setFont("helvetica", "normal");
	doc.setFontSize(8);
	doc.setTextColor(...COLORS.metaKey);
	doc.text(metaLine, leftX, baseY + 12);

	// Logo (logoUrl or logoDataUrl)
	const logoSrc = meta?.logoUrl ?? meta?.logoDataUrl;
	if (logoSrc) {
		try {
			const { dataUrl, w, h } = await svgOrImageToPng(logoSrc, 3);
			const ratio = w > 0 && h > 0 ? w / h : 3;
			const maxW = rightBoxW;
			const maxH = 24;
			let drawW = maxW,
				drawH = drawW / ratio;
			if (drawH > maxH) {
				drawH = maxH;
				drawW = drawH * ratio;
			}
			const logoY = bandY + (bandH - drawH) / 2;
			const logoX = rightX + rightBoxW - drawW;
			pdf.addImage(
				dataUrl,
				"PNG",
				logoX,
				logoY,
				drawW,
				drawH,
				undefined,
				"NONE",
			);
		} catch {
			void 0;
		}
	}

	// Single thin divider
	doc.setDrawColor(...COLORS.rule);
	doc.setLineWidth(0.5);
	const dividerY = margin + bandH + 6;
	doc.line(margin, dividerY, pageWidth - margin, dividerY);

	return dividerY + 10;
}

/* ---------------- Fit-to-width ---------------- */
function estimateColumnWidth(
	header: string,
	sampleValues: string[],
	baseFontSize: number,
	min = 64,
	max = 280,
): number {
	const charPt = baseFontSize * 0.55;
	const headerW = Math.min(max, Math.max(min, header.length * charPt + 18));
	const sampleMaxLen = sampleValues.reduce(
		(m, s) => Math.max(m, (s || "").length),
		0,
	);
	const sampleW = Math.min(max, Math.max(min, sampleMaxLen * charPt + 18));
	return Math.min(max, Math.max(min, Math.max(headerW * 0.8, sampleW * 0.9)));
}
const PDF_MIN_COL_WIDTH = 40;

function fitWidthsToPage(
	headers: string[],
	body: string[][],
	availWidth: number,
	preferredFontSize: number,
	columns?: AnyCol[],
) {
	const MIN_FONT = 7,
		PAD_AT_12PT = 4.2;
	let fontSize = preferredFontSize;
	const colMinWidths =
		columns?.map((c) => {
			const m = c.minWidth;
			return typeof m === "number"
				? Math.max(PDF_MIN_COL_WIDTH, Math.min(m, 160))
				: PDF_MIN_COL_WIDTH;
		}) ?? headers.map(() => PDF_MIN_COL_WIDTH);

	const measureAt = (fs: number) => {
		const widths = headers.map((h, i) => {
			const samples = body.slice(0, 120).map((r) => r[i] ?? "");
			const estimated = estimateColumnWidth(
				h,
				samples,
				fs,
				PDF_MIN_COL_WIDTH,
				200,
			);
			return Math.max(colMinWidths[i] ?? PDF_MIN_COL_WIDTH, estimated);
		});
		return { widths, total: widths.reduce((a, b) => a + b, 0) };
	};
	let { widths, total } = measureAt(fontSize);
	while (total > availWidth && fontSize > MIN_FONT) {
		fontSize = Math.max(MIN_FONT, fontSize - 0.5);
		({ widths, total } = measureAt(fontSize));
	}
	let finalWidths = widths.map((w, i) =>
		Math.max(colMinWidths[i] ?? PDF_MIN_COL_WIDTH, w),
	);
	const totalFinal = finalWidths.reduce((a, b) => a + b, 0);
	if (totalFinal > availWidth) {
		const scale = availWidth / totalFinal;
		finalWidths = finalWidths.map((w) => Math.round(w * scale * 10) / 10);
		const sum = finalWidths.reduce((a, b) => a + b, 0);
		const diff = availWidth - sum;
		if (Math.abs(diff) > 0.5) {
			const idx = finalWidths.findIndex((w) => w >= PDF_MIN_COL_WIDTH + diff);
			if (idx >= 0)
				finalWidths[idx] = Math.round((finalWidths[idx] + diff) * 10) / 10;
		}
	}
	const padding = Math.max(2, (fontSize / 12) * PAD_AT_12PT);
	return { fontSize, padding, finalWidths };
}

/* ---------------- HTML → text + first table ---------------- */
function htmlToPlainLines(html?: string): string[] {
	if (!html) return [];
	try {
		const div = new DOMParser().parseFromString(html, "text/html").body;
		Array.from(div.querySelectorAll("table")).forEach((t) => t.remove());
		Array.from(div.querySelectorAll("br")).forEach((br) =>
			br.replaceWith(document.createTextNode("\n")),
		);
		Array.from(div.querySelectorAll("b,strong")).forEach((el) => {
			const content = el.textContent || "";
			el.replaceWith(document.createTextNode(`**${content}**`));
		});
		const blocks =
			"p,div,li,ul,ol,section,article,header,footer,pre,blockquote,hr";
		Array.from(div.querySelectorAll(blocks)).forEach((el) => {
			if (el !== div && el.lastChild)
				el.appendChild(document.createTextNode("\n"));
		});
		const text = (div.textContent || "")
			.split("\n")
			.map((l) => l.replace(/\u00A0|\u202F/g, " ").trimEnd())
			.join("\n")
			.replace(/\n{3,}/g, "\n\n");
		return text.split("\n");
	} catch {
		return [html.replace(/<[^>]*>/g, "")];
	}
}
/* ---------------- HTML images ---------------- */
function extractImgSrcs(input?: string): string[] {
	if (!input) return [];
	try {
		const div = new DOMParser().parseFromString(input, "text/html").body;
		return Array.from(
			new Set(
				Array.from(div.querySelectorAll("img"))
					.map((i) => i.getAttribute("src") || "")
					.filter(Boolean),
			),
		).slice(0, 2);
	} catch {
		return [];
	}
}
function toAbsoluteForImg(url: string): string {
	try {
		return new URL(url, window.location.origin).toString();
	} catch {
		return url;
	}
}
async function fetchImageAsDataUrl(src: string) {
	try {
		const abs = toAbsoluteForImg(src);
		const res = await fetch(abs, { credentials: "include", mode: "cors" });
		if (!res.ok) throw new Error(String(res.status));
		const blob = await res.blob();
		let naturalW = 0,
			naturalH = 0;
		try {
			const bmp = await createImageBitmap(blob);
			naturalW = bmp.width;
			naturalH = bmp.height;
			bmp.close?.();
		} catch {
			naturalW = 0;
			naturalH = 0;
		}
		const dataUrl = await new Promise<string>((resolve, reject) => {
			const fr = new FileReader();
			fr.onload = () => resolve(typeof fr.result === "string" ? fr.result : "");
			fr.onerror = reject;
			fr.readAsDataURL(blob);
		});
		return { dataUrl, naturalW, naturalH };
	} catch {
		return null;
	}
}
async function loadImageViaCanvas(src: string) {
	return new Promise<{
		dataUrl: string;
		naturalW: number;
		naturalH: number;
	} | null>((resolve) => {
		const img = new Image();
		img.crossOrigin = "anonymous";
		img.onload = () => {
			try {
				const w = img.naturalWidth || img.width || 1,
					h = img.naturalHeight || img.height || 1;
				const c = document.createElement("canvas");
				c.width = w;
				c.height = h;
				const ctx = c.getContext("2d");
				if (!ctx) return resolve(null);
				ctx.drawImage(img, 0, 0);
				resolve({
					dataUrl: c.toDataURL("image/png"),
					naturalW: w,
					naturalH: h,
				});
			} catch {
				resolve(null);
			}
		};
		img.onerror = () => resolve(null);
		img.src = toAbsoluteForImg(src);
	});
}
async function loadImage(src: string) {
	return (await fetchImageAsDataUrl(src)) || (await loadImageViaCanvas(src));
}

/* ---------------- PDF builder ---------------- */
export async function buildPdfShowAllInfo(
	rows: AnyRow[],
	columns: AnyCol[],
	fileName: string,
	pdfMeta?: PdfMeta,
	exportOptions?: PdfExportOptions,
) {
	type CellPaddingShape = {
		top?: number;
		right?: number;
		bottom?: number;
		left?: number;
	};
	const toCellPadding = (value: unknown): CellPaddingShape | null => {
		if (!value || typeof value !== "object") return null;
		const rec = value as Record<string, unknown>;
		return {
			top: typeof rec.top === "number" ? rec.top : undefined,
			right: typeof rec.right === "number" ? rec.right : undefined,
			bottom: typeof rec.bottom === "number" ? rec.bottom : undefined,
			left: typeof rec.left === "number" ? rec.left : undefined,
		};
	};
	const meta = {
		...pdfMeta,
		dateFormat:
			exportOptions?.dateFormat ?? pdfMeta?.dateFormat ?? "DD/MM/YY HH:mm:ss",
		footerText: exportOptions?.footerText ?? pdfMeta?.footerText ?? "Satos",
		truncateAt: exportOptions?.truncateAt ?? pdfMeta?.truncateAt,
		pageSize: exportOptions?.pageSize ?? pdfMeta?.pageSize ?? "a4",
		groupBy: exportOptions?.groupBy,
	};
	const baseCols = (exportOptions?.selectedColumns ?? columns).filter(
		(c) => c.field && !c.hide,
	);
	const alreadyHasAttachment = baseCols.some(isAttachmentCol);
	const dataHasAnyAttachment = rows.some((r) =>
		Boolean(
			r.Filepath ||
			r.FilePath ||
			r.AttachmentUrl ||
			r.AttachmentURL ||
			r.AttachmentLink,
		),
	);
	const visible: AnyCol[] = alreadyHasAttachment
		? baseCols
		: dataHasAnyAttachment
			? [...baseCols, { field: "__attachment", headerName: "Attachment" }]
			: baseCols;

	const headers = visible.map(headerOf);

	// Collect assets for rich cells
	const richColIdx = visible.map(isRichHtmlCol);
	const cellImgSrcs: Record<string, string[]> = {};
	const uniqueSrcs = new Set<string>();
	rows.forEach((r, rIdx) => {
		visible.forEach((c, cIdx) => {
			if (!richColIdx[cIdx]) return;
			const raw = r[c.field];
			const srcs = extractImgSrcs(typeof raw === "string" ? raw : "");
			if (srcs.length) {
				cellImgSrcs[`${rIdx}:${cIdx}`] = srcs;
				srcs.forEach((s) => uniqueSrcs.add(s));
			}
		});
	});
	const imageCache = new Map<
		string,
		{ dataUrl: string; naturalW: number; naturalH: number }
	>();
	await Promise.all(
		Array.from(uniqueSrcs).map(async (s) => {
			const img = await loadImage(s);
			if (img) imageCache.set(s, img);
		}),
	);

	// link cells
	const linkCells = new Map<string, { label: string; url: string }>();

	const truncate = (s: string, max?: number) =>
		max != null && s.length > max ? s.slice(0, max - 3) + "…" : s;
	const formatDate = (v: unknown, fmt: string) => {
		if (v == null) return "";
		const d = dayjs(v);
		return d.isValid() ? d.format(fmt) : asText(v);
	};

	type ColSpanCell = {
		content: string;
		colSpan?: number;
		styles?: UserOptions["styles"];
	};
	const buildRowCells = (r: AnyRow, rIdx: number): (string | ColSpanCell)[] =>
		visible.map((col, cIdx) => {
			if (isAttachmentCol(col) || col.field === "__attachment") {
				const href = resolveAttachmentHref(r);
				if (href) {
					const label = resolveAttachmentLabel(r, href);
					linkCells.set(`${rIdx}:${cIdx}`, { label, url: href });
					return label;
				}
				return "";
			}
			let cellText: string;
			if (isDateCol(col) && meta.dateFormat) {
				const raw =
					r[
						col.field === "DateText" && r.DateRaw != null
							? "DateRaw"
							: col.field
					];
				cellText = formatDate(raw, meta.dateFormat);
			} else {
				const raw = r[col.field];
				if (richColIdx[cIdx] && typeof raw === "string")
					cellText = htmlToPlainLines(raw).join("\n");
				else cellText = asText(raw);
			}
			return truncate(cellText, meta.truncateAt);
		});

	const toSizingCells = (row: RowInput): string[] => {
		const values = Array.isArray(row) ? (row as unknown[]) : [];
		return values.map((cell) =>
			typeof cell === "string" ? cell : asText(cell),
		);
	};

	const body: RowInput[] = [];
	const bodyRowToOrigRow: number[] = [];

	if (meta.groupBy) {
		const groupByCol = visible.find((c) => c.field === meta.groupBy);
		if (groupByCol) {
			const groups = new Map<
				string,
				{ rows: AnyRow[]; origIndices: number[] }
			>();
			rows.forEach((r, origIdx) => {
				const key = asText(r[meta.groupBy!] ?? "").trim() || " ";
				if (!groups.has(key)) groups.set(key, { rows: [], origIndices: [] });
				groups.get(key)!.rows.push(r);
				groups.get(key)!.origIndices.push(origIdx);
			});
			const sortedKeys = [...groups.keys()].sort((a, b) => {
				if (a === " ") return 1;
				if (b === " ") return -1;
				return a.localeCompare(b);
			});
			const groupByHeader = headerOf(groupByCol);
			for (const key of sortedKeys) {
				const { rows: groupRows, origIndices } = groups.get(key)!;
				const label = key === " " ? "(Empty)" : key;
				body.push([
					{
						content: `${groupByHeader}: ${label}`,
						colSpan: headers.length,
						styles: {
							fillColor: COLORS.head,
							textColor: COLORS.headText,
							fontStyle: "bold",
							fontSize: 10,
							cellPadding: 6,
						},
					},
				]);
				bodyRowToOrigRow.push(-1);
				for (let i = 0; i < groupRows.length; i++) {
					body.push(buildRowCells(groupRows[i], body.length));
					bodyRowToOrigRow.push(origIndices[i]);
				}
			}
		} else {
			rows.forEach((r, origIdx) => {
				body.push(buildRowCells(r, body.length));
				bodyRowToOrigRow.push(origIdx);
			});
		}
	} else {
		rows.forEach((r, origIdx) => {
			body.push(buildRowCells(r, body.length));
			bodyRowToOrigRow.push(origIdx);
		});
	}

	const sizingModel: string[][] = body
		.filter((row) => Array.isArray(row) && row.length > 1)
		.map((row) => toSizingCells(row));

	// document
	const doc = new jsPDF({
		orientation: "landscape",
		unit: "pt",
		format: meta.pageSize === "a3" ? "a3" : "a4",
	});
	doc.setProperties({
		title: meta.title || fileName || "Export",
		subject: meta.subtitle || "",
		creator: "Shift Search Export",
		author: meta.footerText || "Satos",
		keywords: "Shift, Handover, Export, PDF",
	});

	const pageWidth = doc.internal.pageSize.getWidth();
	const margin = 36;
	const availWidth = pageWidth - margin * 2;

	const y = await drawHeader(doc, pageWidth, meta, fileName, {
		count: rows.length,
	});

	const preferredFontSize = 10;
	const { fontSize, padding, finalWidths } = fitWidthsToPage(
		headers,
		sizingModel,
		availWidth,
		preferredFontSize,
		visible,
	);

	const columnStyles: NonNullable<UserOptions["columnStyles"]> = {};
	finalWidths.forEach((w, i) => {
		const col = visible[i];
		columnStyles[String(i)] = {
			cellWidth: w,
			...(isCenterAlignCol(col) ? { halign: "center" as const } : {}),
		};
	});

	const tableTotalWidth = finalWidths.reduce((a, b) => a + b, 0);
	const MAX_IMG_H_PT = 88;

	const cellPadding = Math.max(6, padding);
	const imageDrawMap = new Map<
		string,
		{
			src: string;
			drawW: number;
			drawH: number;
			padT: number;
			padLR: number;
		}
	>();
	const opts: UserOptions = {
		startY: y + 4,
		head: [headers],
		body,
		margin: { left: margin, right: margin },
		tableWidth: Math.min(tableTotalWidth, availWidth),
		styles: {
			font: "helvetica",
			fontSize,
			cellPadding: {
				top: cellPadding,
				right: cellPadding + 4,
				bottom: cellPadding,
				left: cellPadding + 4,
			},
			overflow: "linebreak",
			valign: "top",
			lineColor: COLORS.rule,
			lineWidth: 0.25,
			textColor: COLORS.ink,
			minCellHeight: 18,
		},
		headStyles: {
			fillColor: COLORS.head,
			textColor: COLORS.headText,
			fontStyle: "bold",
			fontSize: fontSize + 0.5,
			halign: "left",
			valign: "middle",
			lineWidth: 0.5,
			lineColor: COLORS.headBorder,
			cellPadding: {
				top: cellPadding + 2,
				right: cellPadding + 4,
				bottom: cellPadding + 2,
				left: cellPadding + 4,
			},
		},
		alternateRowStyles: { fillColor: COLORS.rowAlt },
		bodyStyles: { cellWidth: "wrap" },
		columnStyles,
		pageBreak: "auto",
		rowPageBreak: "auto",

		didParseCell: (d: CellHookData) => {
			if (d.section !== "body") return;

			const origRowIdx = bodyRowToOrigRow[d.row.index];
			if (origRowIdx < 0) return;

			// Blue text for attachment columns
			const key = `${d.row.index}:${d.column.index}`;
			if (linkCells.has(key)) d.cell.styles.textColor = COLORS.linkBlue;

			// Reserve space for first inline image in rich cells
			const cIdx = d.column.index;
			const imgSrcs = cellImgSrcs[`${origRowIdx}:${cIdx}`];
			if (!imgSrcs?.length) return;
			const info = imageCache.get(imgSrcs.find((s) => imageCache.has(s)) || "");
			if (!info) return;

			const cellStyles = d.cell.styles as {
				cellPadding?: unknown;
				minCellHeight?: number;
				fontSize?: number | string;
			};
			const padding = toCellPadding(cellStyles.cellPadding);
			const padT = typeof padding?.top === "number" ? padding.top : 4;
			const padLR = typeof padding?.left === "number" ? padding.left : 4;
			const cellW = d.cell.width;
			const ratio =
				info.naturalW > 0 && info.naturalH > 0
					? info.naturalW / info.naturalH
					: 1;
			let drawW = Math.min(cellW - padLR * 2, info.naturalW || cellW);
			let drawH = drawW / (ratio || 1);
			if (drawH > MAX_IMG_H_PT) {
				drawH = MAX_IMG_H_PT;
				drawW = drawH * (ratio || 1);
			}

			const extra = drawH + padT;
			cellStyles.minCellHeight = Math.max(
				cellStyles.minCellHeight || 0,
				(cellStyles.minCellHeight || 0) + extra,
			);
			imageDrawMap.set(`${d.row.index}:${d.column.index}`, {
				src: imgSrcs[0],
				drawW,
				drawH,
				padT,
				padLR,
			});
		},

		didDrawCell: (d: CellHookData) => {
			if (d.section !== "body") return;
			if (bodyRowToOrigRow[d.row.index] < 0) return;
			const pdf = d.doc as jsPDF & {
				getLineHeight?: () => number;
				addImage: (
					imageData: string,
					format: string,
					x: number,
					y: number,
					width: number,
					height: number,
				) => void;
			};

			// draw inline image
			const img = imageDrawMap.get(`${d.row.index}:${d.column.index}`);
			if (img) {
				const cached = imageCache.get(img.src);
				if (cached) {
					const x = d.cell.x + img.padLR,
						y = d.cell.y + img.padT;
					try {
						pdf.addImage(cached.dataUrl, "PNG", x, y, img.drawW, img.drawH);
					} catch {
						return;
					}
				}
			}

			// clickable overlay for attachment cells (first line)
			const cellStyles = d.cell.styles as {
				cellPadding?: unknown;
				minCellHeight?: number;
				fontSize?: number | string;
			};
			const cellPadding = toCellPadding(cellStyles.cellPadding);
			const padLeft =
				typeof cellPadding?.left === "number" ? cellPadding.left : 4;
			const padTop = typeof cellPadding?.top === "number" ? cellPadding.top : 4;
			const fs = Number(cellStyles.fontSize) || pdf.getFontSize();
			const docWithLineHeight = pdf;
			const maybeLineHeight =
				typeof docWithLineHeight.getLineHeight === "function"
					? docWithLineHeight.getLineHeight()
					: undefined;
			const lineH =
				typeof maybeLineHeight === "number" ? maybeLineHeight : fs * 1.15;

			const info = linkCells.get(`${d.row.index}:${d.column.index}`);
			if (!info) return;

			const cellX = Number(d.cell.x);
			const cellY = Number(d.cell.y);
			const cellWidth = Number(d.cell.width);
			const tx = cellX + padLeft;
			const ty = cellY + padTop + fs;
			const maxW = Math.max(0, cellWidth - padLeft - 2);
			const prev = pdf.getFontSize();
			pdf.setFontSize(fs);
			let tw = pdf.getTextWidth(info.label);
			if (tw > maxW) tw = maxW;
			try {
				pdf.link(tx, ty - fs, tw, lineH, { url: info.url });
			} catch {
				return;
			}
			pdf.setFontSize(prev);
		},
	};

	autoTable(doc, opts);

	// Add page footer (branding left, Page X of Y right) on each page
	const totalPages = doc.getNumberOfPages();
	const pageH = doc.internal.pageSize.getHeight();
	const pageW = doc.internal.pageSize.getWidth();
	const footerText = meta.footerText || "Satos";
	doc.setFont("helvetica", "normal");
	doc.setFontSize(8);
	doc.setTextColor(...COLORS.metaKey);
	for (let p = 1; p <= totalPages; p++) {
		doc.setPage(p);
		doc.text(footerText, margin, pageH - 14);
		doc.text(`Page ${p} of ${totalPages}`, pageW - margin, pageH - 14, {
			align: "right",
		});
	}

	doc.save(`${fileName}.pdf`);
}

const DATE_FORMAT_OPTIONS = [
	{ value: "DD/MM/YY HH:mm:ss", label: "DD/MM/YY HH:mm:ss" },
];

/* ---------------- PDF Export Dialog (modern) ---------------- */
export function PdfExportDialog({
	open,
	onClose,
	isExporting,
	cols,
	headerOf,
	selectedFields,
	toggleColumn,
	selectAllColumns,
	deselectAllColumns,
	exportOpts,
	setExportOpts,
	onExport,
	canExport,
}: {
	open: boolean;
	onClose: () => void;
	isExporting: boolean;
	cols: AnyCol[];
	headerOf: (c: AnyCol) => string;
	selectedFields: Set<string>;
	toggleColumn: (f: string) => void;
	selectAllColumns: () => void;
	deselectAllColumns: () => void;
	exportOpts: PdfExportDialogState;
	setExportOpts: React.Dispatch<React.SetStateAction<PdfExportDialogState>>;
	onExport: () => void;
	canExport: boolean;
}) {
	const translation = useContext(TranslationContext);
	const theme = useTheme();
	const subtleSurface =
		theme.palette.mode === "dark"
			? alpha(theme.palette.common.white, 0.025)
			: alpha(theme.palette.common.white, 0.96);
	const mutedSurface =
		theme.palette.mode === "dark"
			? alpha(theme.palette.common.white, 0.04)
			: alpha(theme.palette.common.black, 0.025);
	const sectionCardSx = {
		p: { xs: 1.75, sm: 2 },
		mb: 2,
		borderRadius: 2.5,
		bgcolor: subtleSurface,
		borderColor: alpha(theme.palette.divider, 0.75),
		boxShadow:
			theme.palette.mode === "dark"
				? "inset 0 1px 0 rgba(255,255,255,0.03)"
				: "0 1px 0 rgba(15,23,42,0.02)",
	} as const;
	const fieldSx = {
		"& .MuiOutlinedInput-root": {
			borderRadius: 2,
			backgroundColor: subtleSurface,
			"& fieldset": {
				borderColor: alpha(theme.palette.divider, 0.9),
			},
			"&:hover fieldset": {
				borderColor: alpha(theme.palette.text.primary, 0.28),
			},
			"&.Mui-focused": {
				backgroundColor: theme.palette.background.paper,
				boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.12)}`,
			},
		},
	} as const;
	const sectionTitleSx = {
		display: "block",
		letterSpacing: "0.08em",
		fontWeight: 800,
		fontSize: "0.7rem",
		color: "text.secondary",
		lineHeight: 1.2,
		mb: 0.35,
	} as const;
	const miniActionSx = {
		minWidth: 0,
		px: 1,
		py: 0.5,
		borderRadius: 1.5,
		fontWeight: 700,
		color: "text.secondary",
		border: "1px solid",
		borderColor: alpha(theme.palette.divider, 0.8),
		bgcolor: theme.palette.background.paper,
		"&:hover": {
			color: "text.primary",
			borderColor: alpha(theme.palette.text.primary, 0.2),
			bgcolor: mutedSurface,
		},
	} as const;
	const toggleGroupSx = {
		"& .MuiToggleButton-root": {
			py: 0.8,
			px: 1.5,
			fontWeight: 700,
			color: "text.primary",
			textTransform: "none",
			borderColor: alpha(theme.palette.divider, 0.9),
			bgcolor: theme.palette.background.paper,
			"&.Mui-selected": {
				color: "primary.main",
				bgcolor: alpha(theme.palette.primary.main, 0.1),
			},
		},
	} as const;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				elevation: 0,
				sx: {
					borderRadius: 3,
					border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
					overflow: "hidden",
					bgcolor: "background.paper",
					boxShadow:
						theme.palette.mode === "dark"
							? "0 24px 48px rgba(0,0,0,.4)"
							: "0 24px 48px rgba(15,23,42,.12), 0 8px 24px rgba(15,23,42,.08)",
				},
			}}
		>
			<DialogTitle
				sx={{
					position: "relative",
					display: "flex",
					alignItems: "flex-start",
					gap: 1.5,
					px: { xs: 2, sm: 2.5 },
					pt: 1.75,
					pb: 1.5,
					borderBottom: (t) => `1px solid ${t.palette.divider}`,
					bgcolor: subtleSurface,
				}}
			>
				<Box
					sx={{
						width: 40,
						height: 40,
						borderRadius: 2,
						bgcolor: alpha(theme.palette.info.main, 0.12),
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<PictureAsPdfOutlinedIcon sx={{ color: "info.main", fontSize: 24 }} />
				</Box>
				<Box sx={{ pr: 5 }}>
					<Typography sx={sectionTitleSx}>
						<Translate String="DataGrid.Export">Export</Translate>
					</Typography>
					<Typography
						variant="h6"
						component="h2"
						fontWeight={700}
						color="text.primary"
					>
						<Translate String="DataGrid.Export.Pdf">Export to PDF</Translate>
					</Typography>
					<Typography variant="caption" color="text.secondary">
						<Translate String="DataGrid.Export.Pdf.Subtitle">
							Configure columns and format options
						</Translate>
					</Typography>
				</Box>
				<IconButton
					aria-label={GetLanguageValueAsString(
						translation,
						"Common.Close",
						"Close",
					)}
					onClick={onClose}
					size="small"
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: theme.palette.text.secondary,
						"&:hover": {
							color: theme.palette.text.primary,
							backgroundColor: theme.palette.action.hover,
						},
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{
					pt: 2,
					px: { xs: 2, sm: 2.5 },
					pb: 2,
					bgcolor:
						theme.palette.mode === "dark"
							? alpha(theme.palette.common.black, 0.12)
							: alpha(theme.palette.common.black, 0.02),
				}}
			>
				{/* Columns section */}
				<Card
					variant="outlined"
					sx={sectionCardSx}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							mb: 1.5,
						}}
						>
						<Box>
							<Typography sx={sectionTitleSx}>
								<Translate String="DataGrid.Export.Columns">Columns</Translate>
							</Typography>
							<Typography variant="subtitle2" fontWeight={700} color="text.primary">
								<Translate String="DataGrid.Export.Columns.Heading">
									Choose exported fields
								</Translate>
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<Translate String="DataGrid.Export.Columns.Description">
									Select the columns to include in the PDF output.
								</Translate>
							</Typography>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
							<Chip
								size="small"
								label={`${selectedFields.size} selected`}
								sx={{
									height: 26,
									borderRadius: 999,
									fontWeight: 700,
									bgcolor: mutedSurface,
									color: "text.secondary",
									border: "1px solid",
									borderColor: alpha(theme.palette.divider, 0.75),
								}}
							/>
							<Button
								size="small"
								variant="text"
								onClick={selectAllColumns}
								sx={miniActionSx}
							>
								<Translate String="Common.All">All</Translate>
							</Button>
							<Button
								size="small"
								variant="text"
								onClick={deselectAllColumns}
								sx={miniActionSx}
							>
								<Translate String="Common.None">None</Translate>
							</Button>
						</Box>
					</Box>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: 0.75,
							maxHeight: 100,
							overflowY: "auto",
						}}
					>
						{cols.map((col) => {
							const selected = selectedFields.has(col.field);
							return (
								<Chip
									key={col.field}
									label={headerOf(col)}
									icon={selected ? <CheckRoundedIcon /> : undefined}
									size="small"
									variant={selected ? "filled" : "outlined"}
									onClick={() => toggleColumn(col.field)}
									sx={{
										cursor: "pointer",
										height: 30,
										borderRadius: 1.5,
										fontWeight: selected ? 700 : 600,
										borderWidth: 1,
										borderStyle: "solid",
										borderColor: selected
											? "primary.main"
											: alpha(theme.palette.divider, 0.9),
										bgcolor: selected
											? alpha(theme.palette.primary.main, 0.1)
											: subtleSurface,
										color: selected
											? theme.palette.primary.main
											: theme.palette.text.primary,
										boxShadow: selected
											? `0 0 0 1px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.14)}`
											: "none",
										transition:
											"background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease",
										"& .MuiChip-icon": {
											color: selected
												? theme.palette.primary.main
												: theme.palette.text.secondary,
											fontSize: 16,
										},
										"&:hover": {
											bgcolor: selected
												? alpha(theme.palette.primary.main, 0.16)
												: alpha(theme.palette.primary.main, 0.08),
											borderColor: "primary.main",
										},
									}}
								/>
							);
						})}
					</Box>
				</Card>

				{/* Format options */}
					<Card
						variant="outlined"
						sx={sectionCardSx}
					>
						<Box sx={{ mb: 1.5 }}>
							<Typography sx={sectionTitleSx}>
								<Translate String="Common.Format">Format</Translate>
							</Typography>
							<Typography
								variant="subtitle2"
								fontWeight={700}
								color="text.primary"
							>
								<Translate String="DataGrid.Export.Format.Heading">
									Page and formatting options
								</Translate>
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<Translate String="DataGrid.Export.Format.Description">
									Adjust grouping, dates, and paper size before exporting.
								</Translate>
							</Typography>
						</Box>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
							gap: 2,
						}}
					>
							<FormControl size="small" fullWidth sx={fieldSx}>
								<InputLabel sx={{ color: "text.primary" }}>
									<Translate String="Common.GroupBy">Group by</Translate>
								</InputLabel>
								<Select
									value={exportOpts.groupBy || ""}
									label={GetLanguageValueAsString(
										translation,
										"Common.GroupBy",
										"Group by",
									)}
								onChange={(e) =>
									setExportOpts((o) => ({
										...o,
										groupBy: String(e.target.value),
									}))
								}
							>
									<MenuItem value="">
										<Translate String="Common.None">None</Translate>
									</MenuItem>
								{cols.filter(isGroupableCol).map((col) => (
									<MenuItem key={col.field} value={col.field}>
										{headerOf(col)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
							<FormControl size="small" fullWidth sx={fieldSx}>
								<InputLabel sx={{ color: "text.primary" }}>
									<Translate String="Common.DateFormat">Date format</Translate>
								</InputLabel>
								<Select
									value={exportOpts.dateFormat}
									label={GetLanguageValueAsString(
										translation,
										"Common.DateFormat",
										"Date format",
									)}
								onChange={(e) =>
									setExportOpts((o) => ({
										...o,
										dateFormat: String(e.target.value),
									}))
								}
							>
								{DATE_FORMAT_OPTIONS.map((opt) => (
									<MenuItem key={opt.value} value={opt.value}>
										{opt.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
							<Box>
								<Typography sx={sectionTitleSx}>
									<Translate String="Common.PageSize">Page size</Translate>
								</Typography>
							<ToggleButtonGroup
								value={exportOpts.pageSize}
								exclusive
								onChange={(_, v: string | null) => {
									if (v === "a3" || v === "a4") {
										setExportOpts((o) => ({ ...o, pageSize: v }));
									}
								}}
								size="small"
								sx={toggleGroupSx}
							>
									<ToggleButton value="a4">
										<Translate String="Common.PageSize.A4">A4</Translate>
									</ToggleButton>
									<ToggleButton value="a3">
										<Translate String="Common.PageSize.A3">A3</Translate>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>
						<TextField
							fullWidth
							size="small"
							label={GetLanguageValueAsString(
								translation,
								"DataGrid.Export.FooterText",
								"Footer text",
							)}
							value={exportOpts.footerText}
						onChange={(e) =>
							setExportOpts((o) => ({ ...o, footerText: e.target.value }))
						}
						sx={{ mt: 2, ...fieldSx }}
							placeholder={GetLanguageValueAsString(
								translation,
								"DataGrid.Export.FooterPlaceholder",
								"Satos",
							)}
						/>
					</Card>

				{/* Truncate option */}
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
						gap: 2,
						alignItems: "center",
						p: { xs: 1.5, sm: 1.75 },
						borderRadius: 2.5,
						border: "1px solid",
						borderColor: alpha(theme.palette.divider, 0.75),
						bgcolor: subtleSurface,
					}}
				>
						<Box>
							<Typography sx={sectionTitleSx}>
								<Translate String="Common.Content">Content</Translate>
							</Typography>
						<FormControlLabel
							control={
								<Switch
									checked={exportOpts.truncateEnabled}
									onChange={(e) =>
										setExportOpts((o) => ({
											...o,
											truncateEnabled: e.target.checked,
										}))
									}
									size="small"
								/>
							}
							labelPlacement="start"
								label={
									<Typography variant="body2">
										<Translate String="DataGrid.Export.Truncate">
											Truncate long text
										</Translate>
									</Typography>
								}
							sx={{
								m: 0,
								gap: 1,
								alignItems: "center",
								flexWrap: "nowrap",
								"& .MuiFormControlLabel-label": {
									color: "text.primary",
									marginRight: 0,
								},
							}}
						/>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								<Translate String="DataGrid.Export.Truncate.Description">
									Keep wide descriptions readable in the exported PDF.
								</Translate>
							</Typography>
						</Box>
					{exportOpts.truncateEnabled ? (
						<TextField
							size="small"
							type="number"
							value={exportOpts.truncateAt || 200}
							onChange={(e) =>
								setExportOpts((o) => ({
									...o,
									truncateAt: Math.max(50, parseInt(e.target.value, 10) || 200),
								}))
							}
							sx={{ width: 100, ...fieldSx }}
							inputProps={{ min: 50, max: 500 }}
						/>
					) : (
						<Box />
					)}
				</Box>
			</DialogContent>

			<DialogActions
				sx={{
					px: { xs: 2, sm: 2.5 },
					py: 1.5,
					gap: 1,
					borderTop: (t) => `1px solid ${t.palette.divider}`,
					bgcolor: subtleSurface,
					justifyContent: "space-between",
					flexWrap: "wrap",
				}}
			>
					<Box>
						<Typography variant="body2" color="text.primary" fontWeight={600}>
							<Translate String="DataGrid.Export.Ready">Ready to export</Translate>
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{selectedFields.size}{" "}
							{selectedFields.size === 1 ? (
								<Translate String="DataGrid.Export.FieldSelected">
									field selected
								</Translate>
							) : (
								<Translate String="DataGrid.Export.FieldsSelected">
									fields selected
								</Translate>
							)}
						</Typography>
					</Box>
				<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
					<Button
						onClick={onClose}
						disabled={isExporting}
						sx={{ borderRadius: 2, color: "text.primary", px: 1.75 }}
						>
							<Translate String="Common.Cancel">Cancel</Translate>
						</Button>
					<Button
						variant="contained"
						color="info"
						onClick={onExport}
						disabled={isExporting || !canExport}
						startIcon={
							isExporting ? (
								<CircularProgress size={18} color="inherit" />
							) : (
								<PictureAsPdfOutlinedIcon />
							)
						}
						sx={{ borderRadius: 2, fontWeight: 700, px: 2 }}
					>
						{isExporting ? "Generating…" : "Export PDF"}
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
}

/* ---------------- Export Button ---------------- */
export const CustomExportButton = ({
	rows,
	columns,
	fileName = "Export",
	pdfMeta,
	showPdfOptions = true,
}: Props) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [selectedFields, setSelectedFields] = useState<Set<string>>(
		() => new Set(),
	);
	const [exportOpts, setExportOpts] = useState<PdfExportDialogState>({
		dateFormat: pdfMeta?.dateFormat ?? "DD/MM/YY HH:mm:ss",
		footerText: pdfMeta?.footerText ?? "Satos",
		truncateAt: pdfMeta?.truncateAt ?? 200,
		truncateEnabled: false,
		pageSize: pdfMeta?.pageSize ?? "a4",
		groupBy: "",
	});

	const open = Boolean(anchorEl);

	const safeRows = useMemo(
		() => (Array.isArray(rows) ? [...rows] : []),
		[rows],
	);
	const safeCols = useMemo(
		() => (Array.isArray(columns) ? [...columns] : []),
		[columns],
	);

	const cols = useMemo(
		() =>
			Array.isArray(columns) ? columns.filter((c) => c.field && !c.hide) : [],
		[columns],
	);

	const initSelection = useMemo(() => {
		const set = new Set<string>();
		cols.forEach((c) => set.add(c.field));
		return set;
	}, [cols]);

	const openPdfDialog = () => {
		setSelectedFields(new Set(initSelection));
		setPdfDialogOpen(true);
		setAnchorEl(null);
	};

	const toggleColumn = (field: string) => {
		setSelectedFields((prev) => {
			const next = new Set(prev);
			if (next.has(field)) next.delete(field);
			else next.add(field);
			return next;
		});
	};

	const selectAllColumns = () => setSelectedFields(new Set(initSelection));
	const deselectAllColumns = () => setSelectedFields(new Set());

	const handlePdfExport = async () => {
		const selectedCols = cols.filter((c) => selectedFields.has(c.field));
		if (selectedCols.length === 0) return;
		setIsExporting(true);
		try {
			await buildPdfShowAllInfo(safeRows, safeCols, fileName, pdfMeta, {
				selectedColumns: selectedCols,
				dateFormat: exportOpts.dateFormat,
				footerText: exportOpts.footerText || "Satos",
				truncateAt: exportOpts.truncateEnabled
					? exportOpts.truncateAt || 200
					: undefined,
				pageSize: exportOpts.pageSize,
				groupBy: exportOpts.groupBy || undefined,
			});
			setPdfDialogOpen(false);
		} catch (e) {
			console.error("PDF export failed:", e);
		} finally {
			setIsExporting(false);
		}
	};

	const handleDirectPdf = async () => {
		setIsExporting(true);
		try {
			await buildPdfShowAllInfo(safeRows, safeCols, fileName, pdfMeta);
			setAnchorEl(null);
		} catch (e) {
			console.error("PDF export failed:", e);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<>
			{/* Export trigger (matches other 32px action buttons) */}
			<ProcedureButton
				variant="outlined"
				color="primary"
				size="small"
				startIcon={
					isExporting ? (
						<CircularProgress size={16} />
					) : (
						<DownloadIcon fontSize="small" />
					)
				}
				onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
					setAnchorEl(e.currentTarget)
				}
				disabled={isExporting}
				sx={{
					height: 32,
					minHeight: 32,
					px: 1.5,
					textTransform: "none",
					fontWeight: 700,
					fontSize: "0.875rem",
					borderRadius: 2,
				}}
			>
				<Translate String="DataGrid.Export">Export</Translate>
			</ProcedureButton>

			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				MenuListProps={{ dense: false, sx: { py: 0.5 } }}
				PaperProps={{
					sx: {
						borderRadius: 3,
						mt: 1,
						minWidth: 240,
						overflow: "hidden",
						border: (t) => `1px solid ${t.palette.divider}`,
						boxShadow:
							"0px 14px 30px rgba(0,0,0,0.12), 0px 6px 12px rgba(0,0,0,0.08)",
					},
				}}
			>
				<MenuItem
					sx={{
						mx: 0.75,
						my: 0.25,
						borderRadius: 2,
						px: 1,
						py: 0.75,
						minHeight: 38,
						"& .MuiListItemIcon-root": { minWidth: 30, color: "text.primary" },
						"& .MuiListItemText-primary": {
							fontSize: 14.5,
							fontWeight: 500,
							letterSpacing: 0.1,
						},
						"&:hover": { backgroundColor: "action.hover" },
						"&.Mui-disabled": { opacity: 0.55 },
					}}
					onClick={
						showPdfOptions ? openPdfDialog : () => void handleDirectPdf()
					}
					disabled={isExporting}
				>
					<ListItemIcon>
						{isExporting ? (
							<CircularProgress size={14} />
						) : (
							<PictureAsPdfOutlinedIcon fontSize="small" />
						)}
					</ListItemIcon>
					<ListItemText
						primary={
							isExporting ? (
								<Translate String="DataGrid.Export.Pdf.Generating">
									Generating PDF...
								</Translate>
							) : (
								<Translate String="DataGrid.Export.Pdf.Menu">PDF (.pdf)</Translate>
							)
						}
					/>
				</MenuItem>

				<Divider sx={{ my: 0.5, mx: 1.5 }} />

				{/* close the menu when clicking these custom items */}
				<div onClickCapture={() => setAnchorEl(null)}>
					<CsvExportMenuItemWithIcon
						options={{
							fileName,
							utf8WithBom: true,
							delimiter: ",",
							includeHeaders: true,
						}}
						// keep icons monochrome
						icon={
							<TableViewOutlinedIcon
								fontSize="small"
								sx={{ color: "text.primary" }}
							/>
						}
							label={
								<Translate String="DataGrid.Export.Csv.Menu">CSV (.csv)</Translate>
							}
						secondary={
							<Translate String="DataGrid.Export.Csv.Description">
								Comma-separated file
							</Translate>
						}
						// if this component supports sx, pass it (optional)
						// sx={itemSx}
					/>
				</div>

				<GridExcelExportMenuItemCompat
					options={{ fileName }}
					icon={
						<GridOnOutlinedIcon
							fontSize="small"
							sx={{ color: "text.primary" }}
						/>
					}
					label={
						<Translate String="DataGrid.Export.Excel.Menu">
							Excel (.xlsx)
						</Translate>
					}
				/>
			</Menu>

			{/* PDF Export Options Dialog */}
			<PdfExportDialog
				open={pdfDialogOpen}
				onClose={() => !isExporting && setPdfDialogOpen(false)}
				isExporting={isExporting}
				cols={cols}
				headerOf={headerOf}
				selectedFields={selectedFields}
				toggleColumn={toggleColumn}
				selectAllColumns={selectAllColumns}
				deselectAllColumns={deselectAllColumns}
				exportOpts={exportOpts}
				setExportOpts={setExportOpts}
				onExport={() => void handlePdfExport()}
				canExport={selectedFields.size > 0}
			/>
		</>
	);
};

export default CustomExportButton;
